import React, { useMemo, useState, useEffect } from "react";
import PortfolioReport from "./PortfolioReport";
import NewsletterForm from "../newsletter/NewsletterForm.js";

import {
    collection,
    addDoc,
    serverTimestamp,
    query,
    where,
    getDocs,
} from "firebase/firestore";
import { db } from "../../firebase.js"



/**
 * Stocks With Nik — Investor Quiz Funnel (single-file component)
 * - Multi-step quiz
 * - Produces a structured "profile"
 * - Generates an AI prompt for portfolio construction
 * - Email-gates results (you collect email, then show results)
 *
 * Replace:
 *  - submitLead() with your real API call
 *  - generatePortfolio() with your server-side AI call
 */

const QUIZ = [
    // =========================
    // STEP 1 — GOAL + TIMELINE
    // =========================
    {
        id: "goal",
        group: "Goal",
        title: "What’s your main goal?",
        subtitle: "This decides growth vs dividends vs a balanced mix.",
        helper: "No wrong answer — this just helps us build a portfolio you’ll actually stick with.",
        type: "single",
        options: [
            { label: "Grow my money long-term (10+ years)", value: "growth", score: { growth: 3, income: 0 } },
            { label: "A mix of growth + dividends", value: "balanced", score: { growth: 2, income: 2 } },
            { label: "Monthly income/dividends (cash flow focus)", value: "income", score: { growth: 0, income: 3 } },
        ],
    },
    {
        id: "horizon",
        group: "Goal",
        title: "When do you think you’ll need this money?",
        subtitle: "The sooner you need it, the less risk we should take.",
        helper: "Short timeline = safer plan. Long timeline = can handle more ups and downs.",
        type: "single",
        options: [
            { label: "Less than 3 years", value: "<3", score: { risk: -2, horizon: 1 } },
            { label: "3–7 years", value: "3-7", score: { risk: -1, horizon: 2 } },
            { label: "7–15 years", value: "7-15", score: { risk: 1, horizon: 3 } },
            { label: "15+ years", value: "15+", score: { risk: 2, horizon: 4 } },
        ],
    },

    // =========================
    // STEP 2 — FOUNDATION (MONEY SAFETY)
    // =========================
    {
        id: "bigPurchaseSoon",
        group: "Foundation",
        title: "Are you planning a big purchase in the next 2–3 years?",
        subtitle: "Home, car, wedding, moving, etc.",
        helper: "If you’ll need cash soon, we’ll keep things more stable.",
        type: "single",
        options: [
            { label: "Yes", value: "yes", score: { liquidityNeed: 3, risk: -2 } },
            { label: "Maybe", value: "maybe", score: { liquidityNeed: 1, risk: -1 } },
            { label: "No", value: "no", score: { liquidityNeed: 0, risk: 1 } },
        ],
    },
    {
        id: "emergencyFund",
        group: "Foundation",
        title: "Do you have an emergency fund saved?",
        subtitle: "Money set aside for surprises (job loss, repairs, emergencies).",
        helper: "Ideally: 3–6 months of expenses before going aggressive with investing.",
        type: "single",
        options: [
            { label: "No / less than 1 month", value: "lt1", score: { liquidityNeed: 3, risk: -2, emergencyFund: -2 } },
            { label: "1–3 months", value: "1-3", score: { liquidityNeed: 2, risk: -1, emergencyFund: -1 } },
            { label: "3–6 months", value: "3-6", score: { liquidityNeed: 0, emergencyFund: 1 } },
            { label: "6+ months", value: "6plus", score: { liquidityNeed: 0, risk: 1, emergencyFund: 2 } },
            { label: "Not sure", value: "unsure", score: { liquidityNeed: 1, emergencyFund: 0 } },
        ],
    },
    {
        id: "highInterestDebt",
        group: "Foundation",
        title: "Do you have high-interest debt right now?",
        subtitle: "Examples: credit cards, payday loans, very high APR debt.",
        helper: "Sometimes paying this down is a better “return” than investing.",
        type: "single",
        options: [
            { label: "Yes", value: "yes", score: { debtPressure: 3, risk: -2 } },
            { label: "No", value: "no", score: { debtPressure: 0 } },
            { label: "Not sure", value: "unsure", score: { debtPressure: 1, risk: -1 } },
        ],
    },
    {
        id: "dependents",
        group: "Foundation",
        title: "Do other people depend on your income?",
        subtitle: "Kids, spouse, family members, etc.",
        helper: "More dependents usually means we build a steadier plan.",
        type: "single",
        options: [
            { label: "No", value: "no", score: { riskCapacity: 2, risk: 1 } },
            { label: "Yes (1–2 dependents)", value: "some", score: { riskCapacity: 0 } },
            { label: "Yes (3+ dependents)", value: "many", score: { riskCapacity: -2, risk: -1 } },
        ],
    },

    // =========================
    // STEP 3 — RISK & COMFORT (BEHAVIOR)
    // =========================
    {
        id: "maxDrawdown",
        group: "Risk & Comfort",
        title: "What’s the biggest drop you could handle without selling?",
        subtitle: "This is the most important question for matching your comfort level.",
        helper: "If you pick a low number, we’ll build a smoother portfolio so you’re less likely to panic sell.",
        type: "slider",
        min: 5,
        max: 50,
        step: 5,
        defaultValue: 20,
        score: (value) => {
            if (value <= 10) return { risk: -3, behavior: -2, bondPreference: 2 };
            if (value <= 20) return { risk: -1, behavior: -1, bondPreference: 1 };
            if (value <= 30) return { risk: 1, behavior: 1 };
            if (value <= 40) return { risk: 2, behavior: 2 };
            return { risk: 3, behavior: 2, growth: 1 };
        },
    },
    {
        id: "sleepTest",
        group: "Risk & Comfort",
        title: "Would market ups and downs make you check your account every day?",
        subtitle: "This helps us avoid building a plan that feels stressful.",
        helper: "If investing makes you anxious, a smoother plan usually works better long-term.",
        type: "single",
        options: [
            { label: "Yes — I’d probably stress", value: "yes", score: { behavior: -2, risk: -1, bondPreference: 1 } },
            { label: "Maybe a little", value: "maybe", score: { behavior: -1 } },
            { label: "No — I’m chill long-term", value: "no", score: { behavior: 1, risk: 1 } },
        ],
    },
    {
        id: "trendChasing",
        group: "Risk & Comfort",
        title: "If a stock doubled quickly and everyone was talking about it, what would you do?",
        type: "single",
        options: [
            { label: "Buy it before it goes higher", value: "chase", score: { behavior: -1, risk: 1 } },
            { label: "Research first", value: "research", score: { behavior: 1 } },
            { label: "Ignore it and stick to my plan", value: "ignore", score: { behavior: 2 } },
        ],
    },
    {
        id: "realDrawdown",
        group: "Risk & Comfort",
        title: "During the last market drop… what did you do?",
        subtitle: "We’re not judging — we’re trying to build something realistic.",
        helper: "Your real behavior matters more than your “perfect” answer.",
        type: "single",
        options: [
            { label: "I panic sold", value: "panic", score: { behavior: -3 } },
            { label: "I held and did nothing", value: "hold", score: { behavior: 0 } },
            { label: "I bought more", value: "buy", score: { behavior: 2 } },
            { label: "I wasn’t investing yet", value: "notInvesting", score: { behavior: -1 } },
        ],
    },

    // OPTIONAL: Keep this if you still want it (it overlaps with maxDrawdown + sleepTest)
    // If you feel the quiz is too long, delete this block.
    {
        id: "lostYears",
        group: "Risk & Comfort",
        title: "If your portfolio went nowhere for 2–3 years, what would you do?",
        subtitle: "This is common — markets don’t go up in a straight line.",
        helper: "We’re building a plan you can stick with even when it’s boring.",
        type: "single",
        optional: true,
        options: [
            { label: "I’d probably stop investing", value: "stop", score: { behavior: -2, risk: -1 } },
            { label: "I’d keep contributing normally", value: "keep", score: { behavior: 2, risk: 1 } },
            { label: "I’d contribute more (buying the dip)", value: "more", score: { behavior: 3, risk: 2 } },
        ],
    },

    // =========================
    // STEP 4 — PREFERENCES (WHAT YOU WANT IT TO LOOK LIKE)
    // =========================
    {
        id: "etfVsStocks",
        group: "Preferences",
        title: "How do you want to invest?",
        subtitle: "ETFs = simple + diversified. Stocks = more ups/downs.",
        helper: "If you’re new, ETFs-only is usually the easiest and safest.",
        type: "single",
        options: [
            { label: "ETFs only (keep it simple)", value: "etfsOnly", score: { simplicity: 2, etfsOnly: 2 } },
            { label: "Mostly ETFs + a few stocks", value: "mostlyEtfs", score: { complexity: 1, etfsOnly: 0 } },
            { label: "I want stock picks too", value: "stocksToo", score: { complexity: 2, simplicity: -1 } },
        ],
    },
    {
        id: "satelliteInterest",
        group: "Preferences",
        title: "Do you want a small “high-risk / high-reward” slice?",
        subtitle: "This would always be capped (like 5–10% total).",
        helper: "Optional: the ETFs stay the foundation either way.",
        type: "single",
        showIf: (answers) => answers.etfVsStocks !== "etfsOnly",
        options: [
            { label: "No — keep it ETFs only", value: "no", score: { etfsOnly: 2, simplicity: 1 } },
            { label: "Maybe — if it’s small and controlled", value: "maybe", score: { satelliteInterest: 1 } },
            { label: "Yes — I want a small aggressive slice", value: "yes", score: { satelliteInterest: 2, risk: 1 } },
        ],
    },
    {
        id: "bondSmoothness",
        group: "Preferences",
        title: "Do you want a smoother ride, even if returns may be a bit lower?",
        subtitle: "This helps decide if we add bonds (the “stability” part).",
        helper: "Bonds can reduce big drops, but may lower long-term growth.",
        type: "single",
        options: [
            { label: "Yes — smoother is better", value: "yes", score: { bondPreference: 2, risk: -1 } },
            { label: "Neutral", value: "neutral", score: { bondPreference: 0 } },
            { label: "No — I can handle volatility", value: "no", score: { bondPreference: -1, risk: 1 } },
        ],
    },
    {
        id: "holdingsLimit",
        group: "Preferences",
        title: "How simple do you want your portfolio to be?",
        subtitle: "More holdings = more diversification, but also more complexity.",
        helper: "Most beginners do great with 3–4 ETFs.",
        type: "single",
        options: [
            { label: "Keep it super simple (3–4)", value: "3-4", score: { simplicity: 2, complexity: -1 } },
            { label: "A little more detailed (5–8)", value: "5-8", score: { simplicity: 0, complexity: 1 } },
            { label: "I don’t mind more moving parts (9–12)", value: "9-12", score: { simplicity: -1, complexity: 2 } },
            { label: "Doesn’t matter", value: "any", score: {} },
        ],
    },
    {
        id: "autoInvest",
        group: "Preferences",
        title: "Do you want to automate investing each month?",
        subtitle: "Auto-investing is one of the easiest ways to stay consistent.",
        helper: "This helps us recommend a simple monthly plan.",
        type: "single",
        options: [
            { label: "Yes — automate it", value: "yes", score: { simplicity: 1 } },
            { label: "Maybe", value: "maybe", score: {} },
            { label: "No — I’ll do it manually", value: "no", score: { complexity: 1 } },
        ],
    },
    {
        id: "sectorExclusions",
        group: "Preferences",
        title: "Any industries you want to avoid?",
        subtitle: "Totally optional — this just personalizes the portfolio. It's multiple choice by the way.",
        helper: "This is multiple choice. You can skip this if you don’t care.",
        type: "multi",
        optional: true,
        options: [
            { label: "None", value: "none", score: {} },
            { label: "Tobacco", value: "tobacco", score: {} },
            { label: "Oil & Gas", value: "oil_gas", score: {} },
            { label: "Gambling", value: "gambling", score: {} },
            { label: "Weapons", value: "weapons", score: {} },
        ],
    },

    // =========================
    // STEP 5 — MONEY DETAILS (ACTIONABLE PLAN)
    // =========================
    {
        id: "deposit",
        group: "Money",
        title: "How much are you starting with today?",
        subtitle: "We’ll build something that works even with small amounts.",
        helper: "You can invest with fractional shares — you don’t need a lot to start.",
        type: "single",
        options: [
            { label: "$50–$250", value: "50-250", score: { amount: 1 } },
            { label: "$250–$1,000", value: "250-1000", score: { amount: 2 } },
            { label: "$1,000–$5,000", value: "1000-5000", score: { amount: 3 } },
            { label: "$5,000+", value: "5000+", score: { amount: 4 } },
        ],
    },
    {
        id: "contrib",
        group: "Money",
        title: "How much can you add each month?",
        subtitle: "Monthly contributions matter more than the starting amount.",
        helper: "Consistency beats timing the market.",
        type: "single",
        options: [
            { label: "$0–$50", value: "0-50", score: { contrib: 1 } },
            { label: "$50–$200", value: "50-200", score: { contrib: 2 } },
            { label: "$200–$500", value: "200-500", score: { contrib: 3 } },
            { label: "$500+", value: "500+", score: { contrib: 4 } },
        ],
    },

    // =========================
    // STEP 6 — PERSONAL DETAILS (SMOOTHER RECOMMENDATIONS)
    // =========================
    {
        id: "experience",
        group: "Basics",
        title: "How familiar are you with investing?",
        subtitle: "This helps decide how simple vs detailed your portfolio should be.",
        helper: "If you’re brand new, we’ll keep it super clean and easy.",
        type: "single",
        options: [
            { label: "Brand new", value: "new", score: { complexity: -2 } },
            { label: "I know the basics (ETFs/dividends)", value: "basic", score: { complexity: -1 } },
            { label: "Comfortable (I understand allocation)", value: "comfortable", score: { complexity: 0 } },
            { label: "Advanced (I can rebalance + research)", value: "advanced", score: { complexity: 1 } },
        ],
    },
    {
        id: "incomeStability",
        group: "Basics",
        title: "How stable is your income?",
        subtitle: "This helps decide how much volatility makes sense.",
        helper: "Irregular income usually = steadier investing plan.",
        type: "single",
        options: [
            { label: "Very stable (salary)", value: "stable", score: { risk: 1 } },
            { label: "Somewhat stable", value: "medium", score: { risk: 0 } },
            { label: "Irregular income", value: "irregular", score: { risk: -2 } },
        ],
    },
    {
        id: "accountType",
        group: "Basics",
        title: "What type of account are you investing in?",
        subtitle: "This helps us avoid tax mistakes.",
        helper:
            "If you’re on Robinhood/Fidelity in a normal investing account, that’s usually a taxable brokerage.",
        type: "single",
        options: [
            { label: "Roth IRA", value: "roth", score: { taxAdvantaged: 2 } },
            { label: "Traditional IRA", value: "traditional", score: { taxAdvantaged: 2 } },
            { label: "401(k)", value: "401k", score: { taxAdvantaged: 2 } },
            { label: "Taxable brokerage account", value: "taxable", score: { taxable: 2 } },
            { label: "Not sure yet", value: "unsure", score: {} },
        ],
    },
    {
        id: "age",
        group: "Basics",
        title: "Which age range are you in?",
        subtitle: "This helps fine-tune risk — not to judge you.",
        helper: "Younger investors usually have more time to ride out volatility.",
        type: "single",
        options: [
            { label: "Under 25", value: "<25", score: { horizonBoost: 2 } },
            { label: "25–40", value: "25-40", score: { horizonBoost: 1 } },
            { label: "40–55", value: "40-55", score: { horizonBoost: 0 } },
            { label: "55+", value: "55+", score: { horizonBoost: -2, incomeBias: 1 } },
        ],
    },

    // OPTIONAL: If you still want dividend “comfort” as a separate signal
    // If you want a shorter quiz, you can delete this and just use goal/preference scoring.
    {
        id: "dividendFeeling",
        group: "Preferences",
        title: "Do dividends make you feel more comfortable investing?",
        subtitle: "Some people stay more consistent when they see income coming in.",
        helper: "This only affects how we balance growth vs dividends — not whether you can succeed.",
        type: "single",
        optional: true,
        options: [
            { label: "Yes — I like seeing income", value: "yes", score: { income: 2 } },
            { label: "They’re nice but not required", value: "neutral", score: { income: 1 } },
            { label: "I don’t really care about dividends", value: "no", score: { growth: 1 } },
        ],
    },

    // OPTIONAL: Multi preference “checkboxes” (nice, but can overwhelm beginners)
    // Suggestion: show this only if experience !== "new"
    {
        id: "preferences",
        group: "Preferences",
        title: "Pick what matters most to you.",
        subtitle: "This step adds personalization for what matters to you. It's multiple choice.",
        helper: "If you’re new, you can ignore this and you’ll still get a great plan. It's multiple choice.",
        type: "multi",
        optional: true,
        showIf: (answers) => answers.experience && answers.experience !== "new",
        options: [
            { label: "Simple (3–4 ETFs max)", value: "simple", score: { simplicity: 2 } },
            { label: "More diversification (5–8 holdings)", value: "diverse", score: { simplicity: -1, complexity: 1 } },
            { label: "Dividends matter a lot", value: "dividends", score: { income: 2 } },
            { label: "I’m okay with volatility for growth", value: "volatilityOk", score: { risk: 2, growth: 1 } },
            { label: "I want international exposure", value: "international", score: { international: 2 } },
        ],
    },
];


function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
}

function scoreToRiskLabel(riskScore) {
    // Rough mapping
    if (riskScore <= -3) return "Very Conservative";
    if (riskScore <= -1) return "Conservative";
    if (riskScore <= 2) return "Moderate";
    return "Aggressive";
}

function buildProfile(answers) {
    const totals = {
        risk: 0,
        growth: 0,
        income: 0,
        horizon: 0,
        behavior: 0,
        complexity: 0,
        simplicity: 0,
        international: 0,
        amount: 0,
        contrib: 0,
        horizonBoost: 0,
        incomeStability: 0,
        riskCapacity: 0,
        satelliteInterest: 0,


        // ✅ NEW totals for “deep” quiz
        liquidityNeed: 0,
        debtPressure: 0,
        emergencyFund: 0,
        taxSensitivity: 0,
        etfsOnly: 0,
        bondPreference: 0,
    };

    for (const q of QUIZ) {
        // ✅ If you add branching later
        if (typeof q.showIf === "function" && !q.showIf(answers)) continue;

        const ans = answers[q.id];
        if (ans === undefined || ans === null || ans === "") continue;

        // ✅ 1) Slider / numeric question support
        if (q.type === "slider" || q.type === "number") {
            const value = Number(ans);
            if (Number.isFinite(value) && typeof q.score === "function") {
                const scoreObj = q.score(value) || {};
                for (const [k, v] of Object.entries(scoreObj)) {
                    totals[k] = (totals[k] ?? 0) + v;
                }
            }
            continue;
        }

        // ✅ 2) Single / Multi option scoring (your existing behavior)
        const selectedValues = Array.isArray(ans) ? ans : [ans];

        for (const val of selectedValues) {
            const opt = q.options?.find((o) => o.value === val);
            if (!opt) continue;

            // support either opt.score (object) OR opt.score(value)->object
            const scoreObj =
                typeof opt.score === "function" ? opt.score(val, answers) : opt.score;

            if (!scoreObj) continue;

            for (const [k, v] of Object.entries(scoreObj)) {
                totals[k] = (totals[k] ?? 0) + v;
            }
        }
    }

    const riskScore = clamp(totals.risk, -6, 6);
    const riskLabel = scoreToRiskLabel(riskScore);

    const goal =
        totals.growth > totals.income + 1
            ? "Growth"
            : totals.income > totals.growth + 1
                ? "Income"
                : "Balanced";

    const wantsInternational = totals.international >= 2;

    const complexityPreference =
        totals.simplicity >= 2 || totals.complexity <= -1
            ? "Simple"
            : totals.complexity >= 1
                ? "More diversified"
                : "Standard";

    return {
        riskScore,
        riskLabel,
        goal,
        timeHorizonScore: totals.horizon,
        wantsInternational,
        complexityPreference,

        // keep buckets (existing)
        startingAmountBucket: answers.deposit ?? null,
        monthlyContributionBucket: answers.contrib ?? null,

        // ✅ new raw totals for deep logic later
        rawScores: totals,
        answers,
    };
}


function finalizeInvestorProfile(raw) {
    const rs = raw.rawScores || {};
    const a = raw.answers || {};

    const adjustedRisk =
        (rs.risk ?? 0) +
        (rs.horizonBoost ?? 0) +
        (rs.behavior ?? 0) +
        (rs.incomeStability ?? 0) +
        (rs.riskCapacity ?? 0);

    const riskClamped = clamp(adjustedRisk, -8, 8);

    // normalize slider/numbers
    const maxDrawdown = Number(a.maxDrawdown ?? 0);

    // Core style buckets (allocation guidance)
    const allocationStyle =
        riskClamped <= -3 ? "Capital Preservation" :
            riskClamped <= 1 ? "Moderate" :
                "Growth Oriented";

    const goal =
        (rs.growth ?? 0) > (rs.income ?? 0) + 1 ? "Growth" :
            (rs.income ?? 0) > (rs.growth ?? 0) + 1 ? "Income" :
                "Balanced";

    // Coaching flags
    const foundationRisk =
        (rs.debtPressure ?? 0) >= 2 || (rs.emergencyFund ?? 0) <= -1;

    const panicRisk =
        a.riskFeel === "sell" ||
        a.realDrawdown === "panic" ||
        a.sleepTest === "yes" ||
        maxDrawdown > 0 && maxDrawdown <= 15; // low tolerance

    // Classification: "very aggressive AND stable"
    const veryAggressive =
        riskClamped >= 5 &&
        maxDrawdown >= 30 &&
        a.sleepTest !== "yes" &&
        a.riskFeel !== "sell" &&
        a.realDrawdown !== "panic" &&
        (rs.debtPressure ?? 0) <= 1 &&
        (rs.liquidityNeed ?? 0) <= 1;

    // Permission: only if user wants a satellite slice AND safe enough
    const satelliteInterestScore = rs.satelliteInterest ?? 0;
    const allowSatellite =
        veryAggressive &&
        satelliteInterestScore >= 1 &&      // maybe/yes
        !foundationRisk &&
        !panicRisk;

    const complexityPreference =
        (rs.simplicity ?? 0) >= 2 || (rs.complexity ?? 0) <= -1
            ? "Simple"
            : (rs.complexity ?? 0) >= 1
                ? "More diversified"
                : "Standard";

    return {
        answers: raw.answers,
        rawScores: raw.rawScores,

        // ✅ primary risk score used for allocation + “aggressive user” logic
        riskScore: riskClamped,

        // ✅ label reflects the same score (no mismatch)
        riskLabel: scoreToRiskLabel(riskClamped),

        // optional: keep the “pure risk appetite” label for coaching
        riskLabelCore: scoreToRiskLabel(clamp(rs.risk ?? 0, -6, 6)),

        allocationStyle,
        goal,

        wantsDividends: (rs.income ?? 0) > (rs.growth ?? 0),
        wantsInternational: (rs.international ?? 0) >= 2,

        complexityPreference,

        // Buckets
        accountType: a.accountType,
        age: a.age,
        monthlyContribution: a.contrib,
        startingAmount: a.deposit,

        // Deep signals
        liquidityNeed: rs.liquidityNeed ?? 0,
        debtPressure: rs.debtPressure ?? 0,
        emergencyFundScore: rs.emergencyFund ?? 0,
        bondPreference: rs.bondPreference ?? 0,
        riskCapacity: rs.riskCapacity ?? 0,
        satelliteInterest: satelliteInterestScore,

        // Coaching engine flags
        foundationRisk,
        panicRisk,

        // Stock satellite eligibility
        veryAggressive,
        allowSatellite,
    };
}


/* AI prompt w/ profile data */
function buildAIPrompt(profile) {
    const a = profile.answers || {};
    const rs = profile.rawScores || {};

    // ✅ Explicit coaching flags computed by your JS logic
    const foundationRisk = !!profile.foundationRisk;
    const panicRisk = !!profile.panicRisk;
    const allowSatellite = !!profile.allowSatellite;

    return `
You are "Stocks With Nik" — a beginner-friendly investing coach. Keep it simple, realistic, and non-judgmental.
Create a personalized long-term portfolio plan based on the user’s quiz results.

NON-NEGOTIABLE RULES:
- Use diversified, low-cost ETFs. No leverage. No options strategies. No exotic products.
- Explain in plain English. No jargon without a quick definition.
- Return ONLY valid JSON (no markdown, no extra text).
- Portfolio size:
  - 3–4 holdings if complexityPreference is "Simple"
  - up to 6–8 holdings if "More diversified"

COACHING FLAGS (treat these as TRUE / authoritative):
- foundationRisk: ${foundationRisk ? "true" : "false"}
- panicRisk: ${panicRisk ? "true" : "false"}
- allowSatellite (single stocks allowed): ${allowSatellite ? "true" : "false"}

FOUNDATION RULES:
- If foundationRisk is true, you MUST:
  1) recommend priority order (emergency fund / high-interest debt / then investing),
  2) reduce aggressiveness vs what you would normally do,
  3) include a clear “stability first” note in behaviorWarning OR nextSteps.
- If panicRisk is true, you MUST:
  1) bias toward a smoother allocation (more core/bonds if appropriate),
  2) include a clear behavior coaching warning in behaviorWarning.

SATELLITE (SINGLE STOCK) RULES:
- Only include single-stock satellites if allowSatellite is true.
- If allowSatellite is true:
  - include 1–2 single stocks max,
  - each single stock <= 5%,
  - total single stocks <= 10%,
  - add 1 sentence in summary explaining:
    "These are optional 5% satellite picks; the ETFs are the real foundation."
- If allowSatellite is false:
  - ETFs ONLY. No single stocks. No crypto.
- Never include meme stocks, penny stocks, leveraged ETFs, options strategies, or exotic products.

DESCRIPTION RULE (applies to EVERY allocation item):
- Each allocation item MUST include description:
  - Max 2 sentences.
  - Sentence 1: what it is (plain-English).
  - Sentence 2: why it fits THIS investor.

USER PROFILE (core):
- Primary goal: ${profile.goal ?? "n/a"}
- Allocation style: ${profile.allocationStyle ?? "n/a"}
- Risk tolerance label: ${profile.riskLabel ?? "n/a"} (score: ${profile.riskScore ?? "n/a"})
- Age range: ${profile.age ?? a.age ?? "n/a"}
- Account type: ${profile.accountType ?? a.accountType ?? "n/a"}
- Experience level: ${a.experience ?? "n/a"}
- Time horizon: ${a.horizon ?? "n/a"}
- Wants dividends: ${profile.wantsDividends ? "Yes" : "No"}
- Wants international exposure: ${profile.wantsInternational ? "Yes" : "No"}
- Complexity preference: ${profile.complexityPreference ?? "n/a"}

DEEP FINANCIAL FOUNDATION (details):
- Emergency fund answer: ${a.emergencyFund ?? "n/a"} (score: ${profile.emergencyFundScore ?? rs.emergencyFund ?? "n/a"})
- High-interest debt: ${a.highInterestDebt ?? "n/a"} (pressure score: ${profile.debtPressure ?? rs.debtPressure ?? "n/a"})
- Liquidity need score: ${profile.liquidityNeed ?? rs.liquidityNeed ?? "n/a"}

BEHAVIOR / RISK REALITY (details):
- If portfolio drops 20%: ${a.riskFeel ?? "n/a"}
- Last market drop reaction: ${a.realDrawdown ?? "n/a"}
- Max drawdown tolerated: ${a.maxDrawdown ?? "n/a"}%
- Sleep test (checks daily / stress): ${a.sleepTest ?? "n/a"}
- Income stability: ${a.incomeStability ?? "n/a"}

PREFERENCES / CONSTRAINTS:
- ETFs vs stocks preference: ${a.etfVsStocks ?? "n/a"}
- Smoother ride preference: ${a.bondSmoothness ?? "n/a"} (bond preference score: ${profile.bondPreference ?? rs.bondPreference ?? "n/a"})
- Holdings limit: ${a.holdingsLimit ?? "n/a"}
- Auto-invest monthly: ${a.autoInvest ?? "n/a"}
- Sector exclusions: ${Array.isArray(a.sectorExclusions) ? a.sectorExclusions.join(", ") : (a.sectorExclusions ?? "n/a")}

MONEY DETAILS:
- Starting amount bucket: ${a.deposit ?? "n/a"}
- Monthly contribution bucket: ${a.contrib ?? "n/a"}
- If buckets are ranges, choose a reasonable midpoint and mention it in the note.

ETF UNIVERSE (use these by default):
- US Core: SPY or SPYM (or VOO as alternative)
- Growth: QQQ or QQQM
- Dividends: SCHD
- International: VXUS (only if they want international)
Optional (only if conservative / shorter horizon / smoother preference is strong):
- Bonds: BND (or similar broad bond ETF)

SINGLE STOCK UNIVERSE (ONLY if allowSatellite is true):
- Examples: NVDA, GOOGL, MSFT, AMZN, META
- Pick 1–2 that match the user's goal and keep them small.
- (If you choose to include Bitcoin exposure, use a spot Bitcoin ETF and set kind="ETF", not CRYPTO.)

OUTPUT JSON SHAPE (exact keys):
{
  "investorType": "string (branded name like 'The Steady Builder')",
  "summary": "2-3 sentences",
  "allocation": [
    {
      "name": "State Street SPDR S&P 500 ETF Trust",
      "ticker": "SPY",
      "percent": 60,
      "role": "Core US",
      "why": "short reason",
      "kind": "ETF",
      "description": "Max 2 sentences. Sentence 1=what it is. Sentence 2=why it fits this investor."
    }
  ],
  "initialPlan": [
    { "name": "State Street SPDR S&P 500 ETF Trust", "ticker": "SPY", "amount": 600, "note": "...", "description": "..." }
  ],
  "monthlyPlan": [
    { "ticker": "SPY", "amount": 120, "description": "..." }
  ],
  "rebalance": {
    "frequency": "every 6-12 months",
    "rule": "rebalance if any holding drifts by 5%+"
  },
  "behaviorWarning": "1-2 sentences tailored to behavior + stability if applicable",
  "nextSteps": [
    "3-7 actionable steps to set this up"
  ],
  "risks": [
    "2-5 short bullets"
  ],
  "disclaimer": "1 sentence"
}

VALIDATION RULES:
- allocation percents must sum to 100.
- Use integer percents.
- initialPlan + monthlyPlan should roughly match allocation using reasonable numbers.
- If allowSatellite is true:
  - 1–2 single stocks max
  - each <= 5%
  - total single stocks <= 10%
- If allowSatellite is false:
  - 0 single stocks
`.trim();
}

/** Adds email to firebase collection (skip if exists) */
async function submitLead({ email }) {
    try {
        const normalized = email.trim().toLowerCase();

        const emailsRef = collection(db, "email-list");

        // 🔍 Check if email already exists
        const q = query(emailsRef, where("email", "==", normalized));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
            console.log("Email already exists. Skipping write.");
            return { status: "exists" };
        }

        // ✅ Add new document with random ID
        await addDoc(emailsRef, {
            email: normalized,
            source: "quiz_portfolio",
            createdAt: serverTimestamp(),
        });

        console.log("Email added to database.");
        return { status: "created" };

    } catch (e) {
        console.error("Error adding document: ", e);
        throw e;
    }
}

/** Replace with your server-side AI call */
async function generatePortfolio({ profile }) {
    const prompt = buildAIPrompt(profile);

    const res = await fetch("https://stocks-with-nik-api.onrender.com/generate-portfolio", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile, prompt }),
    });


    if (!res.ok) throw new Error("Failed to generate");
    return res.json();
}


export default function InvestorQuizFunnel() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [email, setEmail] = useState("");
    const [leadSubmitted, setLeadSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [portfolio, setPortfolio] = useState(null);
    const [error, setError] = useState("");

    const [submittedOnce, setSubmittedOnce] = useState(false); // locks button forever (or until reset)
    const [success, setSuccess] = useState(false);             // optional for UI messaging


    const current = QUIZ[step];

    useEffect(() => {
        const q = QUIZ[step];
        if (q?.type !== "slider") return;

        setAnswers((prev) => {
            if (prev[q.id] !== undefined) return prev;
            return { ...prev, [q.id]: q.defaultValue ?? q.min };
        });
    }, [step]);


    const progress = Math.round(((step + 1) / QUIZ.length) * 100);

    const rawProfile = useMemo(() => buildProfile(answers), [answers]);

    const profile = useMemo(
        () => finalizeInvestorProfile(rawProfile),
        [rawProfile]
    );

    const prompt = useMemo(() => buildAIPrompt(profile), [profile]);

    function setSingleAnswer(qid, value) {
        setAnswers((prev) => ({ ...prev, [qid]: value }));
    }

    function toggleMultiAnswer(qid, value) {
        setAnswers((prev) => {
            const cur = Array.isArray(prev[qid]) ? prev[qid] : [];

            // Special case: "none" clears everything else
            if (value === "none") return { ...prev, [qid]: ["none"] };

            // If selecting something else, remove "none"
            const withoutNone = cur.filter((v) => v !== "none");

            const next = withoutNone.includes(value)
                ? withoutNone.filter((v) => v !== value)
                : [...withoutNone, value];

            return { ...prev, [qid]: next };
        });
    }


    function canGoNext() {
        const a = answers[current.id];

        if (current.type === "slider") return a !== undefined && a !== null;
        if (current.type === "single") return !!a;
        if (current.type === "multi") return Array.isArray(a) && a.length > 0;

        return false;
    }


    async function onSubmitEmailAndGenerate() {
        setError("");
        if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
            setError("Please enter a valid email.");
            return;
        }

        try {
            setLoading(true);


            if (!leadSubmitted) {
                const result = await submitLead({ email });
                setLeadSubmitted(true);
            }

            const res = await generatePortfolio({ profile });
            setPortfolio(res);
            console.log(res)

            // ✅ lock the button once portfolio exists
            setSubmittedOnce(true);
            setSuccess(true);

            // 2) Email it
            const emailRes = await fetch("https://stocks-with-nik-api.onrender.com/email-portfolio", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    to: email,
                    // IMPORTANT: use the fresh response, not React state
                    portfolio: res.portfolio ?? res,
                }),
            });

            if (!emailRes.ok) {
                const err = await emailRes.json().catch(() => ({}));
                throw new Error(err?.error || "Email failed");
            }


            console.log("Email sent ✅");

        } catch (e) {
            setError("Something went wrong. Please try again.");
            console.log(e)
        } finally {
            setLoading(false);
        }
    }

    const isLastQuestion = step === QUIZ.length - 1;

    return (
        <>
            <div style={{ maxWidth: 760, margin: "0 auto", padding: 20, fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial" }}>
            
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 16, padding: 18 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                        <div>
                            <div style={{ fontSize: 14, opacity: 0.75 }}>Stocks With Nik</div>
                            <div style={{ fontSize: 22, fontWeight: 700 }}>Build your beginner investing profile</div>
                        </div>
                        <div style={{ fontSize: 14, opacity: 0.8 }}>{progress}%</div>
                    </div>

                    <div style={{ height: 8, background: "#f3f4f6", borderRadius: 999, marginTop: 12, overflow: "hidden" }}>
                        <div style={{ height: 8, width: `${progress}%`, background: "#111827" }} />
                    </div>

                    {/* Quiz */}
                    {!isLastQuestion || !canGoNext() ? (
                        <div style={{ marginTop: 18 }}>
                            <div style={{ fontSize: 18, fontWeight: 700 }}>{current.title}</div>
                            {current.subtitle && <div style={{ marginTop: 6, opacity: 0.75 }}>{current.subtitle}</div>}

                            <div style={{ marginTop: 14 }}>
                                {current.type === "slider" ? (
                                    <div>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                fontSize: 14,
                                                opacity: 0.8,
                                            }}
                                        >
                                            <span>{current.min}%</span>
                                            <span>
                                                <b>{answers[current.id] ?? current.defaultValue}%</b>
                                            </span>
                                            <span>{current.max}%</span>
                                        </div>

                                        <input
                                            type="range"
                                            min={current.min}
                                            max={current.max}
                                            step={current.step}
                                            value={answers[current.id] ?? current.defaultValue}
                                            onChange={(e) => setSingleAnswer(current.id, Number(e.target.value))}
                                            style={{ width: "100%", marginTop: 10 }}
                                        />

                                        <div style={{ marginTop: 10, opacity: 0.75 }}>
                                            Tip: if you pick a low number here, we’ll build a smoother portfolio so
                                            you’re less likely to panic sell.
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: "grid", gap: 10 }}>
                                        {current.options.map((opt) => {
                                            const selected =
                                                current.type === "single"
                                                    ? answers[current.id] === opt.value
                                                    : Array.isArray(answers[current.id]) &&
                                                    answers[current.id].includes(opt.value);

                                            return (
                                                <button
                                                    key={opt.value}
                                                    onClick={() =>
                                                        current.type === "single"
                                                            ? setSingleAnswer(current.id, opt.value)
                                                            : toggleMultiAnswer(current.id, opt.value)
                                                    }
                                                    style={{
                                                        textAlign: "left",
                                                        padding: 14,
                                                        borderRadius: 12,
                                                        border: selected ? "2px solid #111827" : "1px solid #e5e7eb",
                                                        background: selected ? "#f9fafb" : "white",
                                                        cursor: "pointer",
                                                        fontSize: 15,
                                                    }}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>


                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 18 }}>
                                <button
                                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                                    disabled={step === 0}
                                    style={{
                                        padding: "10px 14px",
                                        borderRadius: 10,
                                        border: "1px solid #e5e7eb",
                                        background: step === 0 ? "#f9fafb" : "white",
                                        cursor: step === 0 ? "not-allowed" : "pointer",
                                    }}
                                >
                                    Back
                                </button>

                                <button
                                    onClick={() => setStep((s) => Math.min(QUIZ.length - 1, s + 1))}
                                    disabled={!canGoNext()}
                                    style={{
                                        padding: "10px 14px",
                                        borderRadius: 10,
                                        border: "1px solid #111827",
                                        background: canGoNext() ? "#111827" : "#9ca3af",
                                        color: "white",
                                        cursor: canGoNext() ? "pointer" : "not-allowed",
                                    }}
                                >
                                    Next
                                </button>
                            </div>

                            {/* Mini profile preview (optional) */}
                            <div style={{ marginTop: 18, padding: 14, borderRadius: 12, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
                                <div style={{ fontWeight: 700, marginBottom: 6 }}>Current profile</div>
                                <div style={{ opacity: 0.85, lineHeight: 1.5 }}>
                                    Goal: <b>{profile.goal}</b> • Risk: <b>{profile.riskLabel}</b> • International:{" "}
                                    <b>{profile.wantsInternational ? "Yes" : "No"}</b> • Style: <b>{profile.complexityPreference}</b>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Email gate + results
                        <div style={{ marginTop: 18 }}>
                            <div style={{ fontSize: 18, fontWeight: 700 }}>Last step — where should I send your free portfolio?</div>
                            <div style={{ marginTop: 6, opacity: 0.75 }}>
                                You’ll get your personalized portfolio + a simple explanation of why it fits your answers.
                            </div>

                            <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
                                <input
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@email.com"
                                    style={{
                                        padding: 12,
                                        borderRadius: 10,
                                        border: "1px solid #e5e7eb",
                                        fontSize: 15,
                                    }}
                                />

                                <button
                                    onClick={onSubmitEmailAndGenerate}
                                    disabled={loading || submittedOnce || !!portfolio}
                                    style={{
                                        padding: 12,
                                        borderRadius: 10,
                                        border: "1px solid #111827",
                                        background: "#111827",
                                        color: "white",
                                        cursor: loading || submittedOnce || portfolio ? "not-allowed" : "pointer",
                                        fontSize: 15,
                                        opacity: loading || submittedOnce || portfolio ? 0.6 : 1,
                                    }}
                                >
                                    {loading
                                        ? "Generating...please wait..."
                                        : submittedOnce || portfolio
                                            ? "Portfolio generated ✅"
                                            : "Get my free portfolio"}
                                </button>

                                {loading && <div><p>Please don't leave this page or refresh. Tailoring the portfolio takes a few moments.</p></div>}           
                                {error && <div style={{ color: "#b91c1c" }}>{error}</div>}
                            </div>

                            <div style={{ marginTop: 16, padding: 14, borderRadius: 12, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
                                <div style={{ fontWeight: 700, marginBottom: 6 }}>Your investor profile</div>
                                <div style={{ opacity: 0.85, lineHeight: 1.6 }}>
                                    <div>
                                        <b>Goal:</b> {profile.goal}
                                    </div>
                                    <div>
                                        <b>Risk tolerance:</b> {profile.riskLabel}
                                    </div>
                                    <div>
                                        <b>International exposure:</b> {profile.wantsInternational ? "Yes" : "No"}
                                    </div>
                                    <div>
                                        <b>Complexity:</b> {profile.complexityPreference}
                                    </div>
                                </div>
                            </div>

                            {portfolio?.portfolioMarkdown && (
                                <div style={{ marginTop: 16, padding: 14, borderRadius: 12, border: "1px solid #e5e7eb" }}>
                                    <div style={{ fontWeight: 700, marginBottom: 8 }}>Your portfolio</div>
                                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{portfolio.portfolioMarkdown}</pre>
                                </div>
                            )}
                        </div>
                    )}
                </div>

            </div>
            {portfolio && (
                <>
                <div style={{ marginTop: 16 }}>
                    <PortfolioReport portfolioData={portfolio} fileName="stocks-with-nik-portfolio.pdf" />
                </div>
                <NewsletterForm />
                </>
            )}
        </>
    );
}
